import cx from 'classnames';
import { Ref, forwardRef, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterType, FilterValue } from '@/models';
import { CloseIcon } from '@/components/base/Icons/RocheIconPack';
import FilterChipTooltip from '@/storybook/components/FilterChipTooltip/FilterChipTooltip';
import IconButton from '@/storybook/components/IconButton/IconButton';
import { translateDynamicKey } from '@/types/translation';
import { getFilterTypeLabelKey } from '@/utils/filter/getFilterTypeLabelKey';
import styles from './ActiveFilter.module.scss';
import useFormattedGlobalFilterValue from './hooks/useFormattedGlobalFilterValue';
import { useGetActiveFilterAdditionalText } from './hooks/useGetActiveFilterAdditionalText';

interface ActiveFilterProps {
  filterKey: FilterType | string;
  value: FilterValue;
  onDelete?: () => void;
  className?: string;
}

const ActiveFilter = ({ filterKey, value, onDelete, className }: ActiveFilterProps, ref: Ref<HTMLDivElement>) => {
  const { t } = useTranslation();
  const formattedValue = useFormattedGlobalFilterValue({ filterKey, value });
  const filterAdditionalText = useGetActiveFilterAdditionalText(filterKey);
  const tooltipId = useId();
  const tooltipLabelKey = getFilterTypeLabelKey(filterKey);
  const tooltip = tooltipLabelKey ? translateDynamicKey(t, tooltipLabelKey) : undefined;

  return (
    <div
      ref={ref}
      className={cx(styles.activeFilterContainer, className)}
      data-tooltip-id={tooltip ? tooltipId : undefined}
    >
      <span>
        <div className={styles.labelWrapper}>{formattedValue}</div>
      </span>
      {filterAdditionalText && <span className={styles.additionalText}>{filterAdditionalText}</span>}
      {onDelete && (
        <IconButton
          appearance="text"
          size="S"
          icon={<CloseIcon />}
          onClick={onDelete}
          className={styles.closeButton}
          aria-label={t('global.filterChip.removeAriaLabel')}
        />
      )}
      {tooltip ? <FilterChipTooltip id={tooltipId} content={tooltip} /> : null}
    </div>
  );
};

export default forwardRef(ActiveFilter);
